import React, { useEffect, useState } from "react";
import { Tabs, Tab, Box } from "@mui/material";
import {
  useNavigate,
  useOutletContext,
  useParams,
  useSearchParams,
} from "react-router-dom";
import GlobalLoader from "../../common/GlobalLoader";
import EmptyPage from "../../common/emptyPage";
import "../../../styles/teacher/homework.scss";
import fileIcon from "../../../assets/svg/pdfIcon.svg";
import openFolder from "../../../assets/png/openFolderIcon.png";
import { getSubjectsInstitution } from "../../../helper/apicalls/institution/institutionCourse";
import { AddIcon } from "../../../icons";
import GenerateHomeworkPopup from "../../common/generatehomework";
import DeleteConfirmPopUp from "../../common/deleteConfirmPopUp";
import {
  getHomeworks,
  deleteHomework,
  getCompletedStudents,
} from "../../../helper/apicalls/homework";
import HomeWrokIcon from "../../../assets/png/homework.png";

const HomeworkSubjects = () => {
  const navigate = useNavigate();
  const { key: courseKey } = useParams();
  const [searchParams] = useSearchParams();
  const batchId = searchParams.get("batch");
  const { setHeaderName, setIsShowSearch } = useOutletContext();
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [homeworks, setHomeworks] = useState([]);
  const [activeTab, setActiveTab] = useState("homework");

  const fetchSubjects = async () => {
    if (!courseKey) return;
    setLoading(true);
    try {
      const data = await getSubjectsInstitution(courseKey);
      setSubjects(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Failed to fetch subjects (homework):", e);
      setSubjects([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchHomeworks = async () => {
    try {
      const res = await getHomeworks(undefined, { batch: batchId });
      let list = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res)
          ? res
          : [];
      if (batchId) {
        const hasBatchField = list.some(
          (hw) => hw?.batch || hw?.batch_id || hw?.batchId,
        );
        if (hasBatchField) {
          list = list.filter(
            (hw) => (hw?.batch || hw?.batch_id || hw?.batchId) === batchId,
          );
        }
      }
      setHomeworks(list);
    } catch (e) {
      console.error("Failed to fetch homeworks:", e);
      setHomeworks([]);
    }
  };

  const [isGeneratorOpen, setIsGeneratorOpen] = useState(false);
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [completedOpenId, setCompletedOpenId] = useState(null);
  const [completedLoading, setCompletedLoading] = useState(false);
  const [completedError, setCompletedError] = useState("");
  const [completedStudents, setCompletedStudents] = useState([]);

  useEffect(() => {
    const handleDocClick = (e) => {
      const t = e.target;
      if (
        t.closest &&
        (t.closest(".course-actions-menu") ||
          t.closest(".course-actions") ||
          t.closest(".action-btn"))
      ) {
        return;
      }
      if (menuOpenId !== null) setMenuOpenId(null);
      if (completedOpenId !== null) setCompletedOpenId(null);
    };
    document.addEventListener("mousedown", handleDocClick);
    return () => document.removeEventListener("mousedown", handleDocClick);
  }, [menuOpenId, completedOpenId]);

  const handleCompletedClick = async (hw, e) => {
    e.preventDefault();
    e.stopPropagation();
    const id = hw?.id || hw?._id || hw?.key;
    if (!id) return;
    const qp = new URLSearchParams();
    qp.set("id", String(id));
    if (courseKey) qp.set("course", String(courseKey));
    if (batchId) qp.set("batch", String(batchId));
    qp.set("view", "completed");
    qp.set("level", "course");
    navigate(`/teacher/course/homework/detail?${qp.toString()}`);
  };

  const fetchSubjectsAndHomeworks = async () => {
    setLoading(true);
    try {
      const [subsData, res] = await Promise.all([
        courseKey ? getSubjectsInstitution(courseKey) : Promise.resolve([]),
        getHomeworks(undefined, { batch: batchId }),
      ]);
      setSubjects(Array.isArray(subsData) ? subsData : []);

      let list = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res)
          ? res
          : [];
      if (batchId) {
        const hasBatchField = list.some(
          (hw) => hw?.batch || hw?.batch_id || hw?.batchId,
        );
        if (hasBatchField) {
          list = list.filter(
            (hw) => (hw?.batch || hw?.batch_id || hw?.batchId) === batchId,
          );
        }
      }
      setHomeworks(list);
    } catch (e) {
      console.error("Failed to fetch subjects/homeworks (homework):", e);
      setSubjects([]);
      setHomeworks([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddHomework = () => {
    setIsGeneratorOpen(true);
  };

  const handleHomeworkClick = (hw) => {
    const id = hw?.id || hw?._id || hw?.key;
    if (!id) return;
    const qp = new URLSearchParams();
    qp.set("id", String(id));
    if (courseKey) qp.set("course", String(courseKey));
    if (batchId) qp.set("batch", String(batchId));
    navigate(`/teacher/course/homework/detail?${qp.toString()}`);
  };

  const handleCardClick = (hw, isOverdue) => {
    const id = hw?.id || hw?._id || hw?.key;
    if (!id) return;
    const qp = new URLSearchParams();
    qp.set("id", String(id));
    if (courseKey) qp.set("course", String(courseKey));
    if (batchId) qp.set("batch", String(batchId));
    if (isOverdue) {
      qp.set("view", "completed");
      qp.set("level", "course");
    }
    navigate(`/teacher/course/homework/detail?${qp.toString()}`);
  };

  const handleEdit = (hw, e) => {
    e.stopPropagation();
    setMenuOpenId(null);
    const id = hw?.id || hw?._id || hw?.key;
    if (!id) return;
    const qp = new URLSearchParams();
    qp.set("id", String(id));
    if (courseKey) qp.set("course", String(courseKey));
    if (batchId) qp.set("batch", String(batchId));
    qp.set("edit", "1");
    navigate(`/teacher/course/homework/detail?${qp.toString()}`);
  };

  const promptDelete = (hw, e) => {
    e.stopPropagation();
    const id = hw?.id || hw?._id || hw?.key;
    if (!id) return;
    setDeleteTargetId(id);
    setIsConfirmDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTargetId) return;
    try {
      setDeletingId(deleteTargetId);
      await deleteHomework(deleteTargetId, "course");
      await fetchHomeworks();
    } catch (err) {
      console.error("Failed to delete homework:", err);
    } finally {
      setDeletingId(null);
      setMenuOpenId(null);
      setIsConfirmDeleteOpen(false);
      setDeleteTargetId(null);
    }
  };

  const handleSubjectClick = (subject) => {
    if (!subject?.key || !courseKey) return;
    const qp = batchId ? `?batch=${encodeURIComponent(batchId)}` : "";
    navigate(
      `/teacher/homework/course/${courseKey}/subject/${subject.key}${qp}`,
    );
  };

  useEffect(() => {
    setHeaderName && setHeaderName("Homework");
    setIsShowSearch && setIsShowSearch(false);
  }, [setHeaderName, setIsShowSearch]);

  useEffect(() => {
    fetchSubjectsAndHomeworks();
  }, [courseKey, batchId]);

  return (
    <div className="homework-container">
      <GlobalLoader visible={loading} text="Loading..." />
      <div className="hw-card">
        <div className="hw-card-header hw-tabs-row">
          <Box className="hw-tabs-center">
            <Tabs
              className="hw-tabs"
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              centered
            >
              {/* <Tab label="Subjects" value="structure" /> */}
              <Tab label="Homework" value="homework" />
            </Tabs>
          </Box>
          {activeTab === "homework" && (
            <div className="hw-card-actions">
              <button
                type="button"
                className="hw-add-btn"
                onClick={handleAddHomework}
              >
                <AddIcon className="action-button-icon" />
                <span>Add Homework</span>
              </button>
            </div>
          )}
        </div>
        <div className="hw-grid">
          {activeTab === "homework" &&
            homeworks?.map((hw) => {
              const id = hw?.id || hw?._id || hw?.key;
              const hasDeadline = Boolean(hw?.deadline);
              const dl = hasDeadline ? new Date(hw.deadline) : null;
              const todayStart = new Date();
              todayStart.setHours(0, 0, 0, 0);
              const isOverdue = hasDeadline
                ? dl.setHours(0, 0, 0, 0) < todayStart.getTime()
                : false;
              return (
                <div
                  key={id}
                  className={`hw-item${isOverdue ? " overdue" : ""}`}
                  onClick={() => handleCardClick(hw, isOverdue)}
                  style={{ position: "relative" }}
                >
                  <div className="folder-icon">
                    <img src={HomeWrokIcon} alt="file" className="icon-file" />
                  </div>
                  {completedOpenId === id && (
                    <div
                      className="course-actions-menu"
                      style={{
                        position: "absolute",
                        top: 32,
                        right: 40,
                        zIndex: 3,
                        minWidth: 220,
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div style={{ padding: 4, fontWeight: 600 }}>
                        Completed Students
                      </div>
                      {completedLoading && (
                        <div className="actions-item">Loading...</div>
                      )}
                      {!completedLoading && completedError && (
                        <div className="actions-item">{completedError}</div>
                      )}
                      {!completedLoading &&
                        !completedError &&
                        (Array.isArray(completedStudents) &&
                        completedStudents.length > 0 ? (
                          completedStudents.slice(0, 10).map((s) => (
                            <div key={s?.id || s?._id} className="actions-item">
                              {s?.name ||
                                `${s?.first_name || ""} ${s?.last_name || ""}`.trim() ||
                                "Student"}
                            </div>
                          ))
                        ) : (
                          <div className="actions-item">
                            No completed students
                          </div>
                        ))}
                    </div>
                  )}
                  <div className="hw-item-name">
                    {hw?.name || hw?.title || "Homework"}
                  </div>
                  {hw?.deadline && (
                    <div className="hw-item-subtitle">
                      {new Date(hw.deadline).toLocaleDateString()}
                    </div>
                  )}
                  <div
                    className="course-actions"
                    style={{ position: "absolute", top: 8, right: 8 }}
                  >
                    {hasDeadline && (
                      <span
                        className={`status-dot ${isOverdue ? "red" : "green"}`}
                        title={
                          isOverdue ? "View completed students" : "On track"
                        }
                        style={{ cursor: "default" }}
                      />
                    )}
                    <button
                      className="action-btn"
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuOpenId(menuOpenId === id ? null : id);
                      }}
                      aria-label="More actions"
                    >
                      ⋮
                    </button>
                  </div>
                  {menuOpenId === id && (
                    <div
                      className="course-actions-menu"
                      style={{
                        position: "absolute",
                        top: 32,
                        right: 8,
                        zIndex: 2,
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        type="button"
                        className="actions-item"
                        onClick={(e) => handleEdit(hw, e)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="actions-item danger"
                        onClick={(e) => promptDelete(hw, e)}
                        disabled={deletingId === id}
                      >
                        {deletingId === id ? "Deleting..." : "Delete"}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          {isConfirmDeleteOpen && (
            <DeleteConfirmPopUp
              isConfirmDeleteOpen={isConfirmDeleteOpen}
              setIsConfirmDeleteOpen={setIsConfirmDeleteOpen}
              handleDelete={confirmDelete}
              title="Delete Homework"
              message="Are you sure you want to delete this homework? This action cannot be undone."
              confirmLabel={deletingId ? "Deleting..." : "Delete"}
            />
          )}
          {activeTab === "structure" &&
            !loading &&
            subjects?.map((sub) => (
              <div
                key={sub?.id || sub?.key}
                className="hw-item"
                onClick={() => handleSubjectClick(sub)}
              >
                <div className="folder-icon">
                  <img
                    src={openFolder}
                    alt="openFolder"
                    className="icon-folder-lg"
                  />
                </div>
                <div className="hw-item-name">{sub?.name}</div>
              </div>
            ))}
        </div>
        {activeTab === "structure" && !loading && subjects?.length === 0 && (
          <EmptyPage content={"No subjects Available"} />
        )}
        {activeTab === "homework" && !loading && homeworks?.length === 0 && (
          <EmptyPage content={"No homeworks Available"} />
        )}
      </div>
      {isGeneratorOpen && (
        <GenerateHomeworkPopup
          open={isGeneratorOpen}
          handleClose={() => setIsGeneratorOpen(false)}
          onSave={() => {
            fetchHomeworks();
            setIsGeneratorOpen(false);
          }}
          context="course"
          contextKey={courseKey}
          batchId={batchId}
        />
      )}
    </div>
  );
};

export default HomeworkSubjects;
